import { useEffect, useState } from 'preact/hooks';
import QRCode from 'qrcode';
import type { PaymentOption, PublicSession } from '../api/types';
import { CHAINS, checkTokenContract } from '../chains';
import { t } from '../i18n';
import { copyText } from '../utils/clipboard';
import { trimDecimal, isMobile } from '../utils/format';
import { checkOption, type ManifestCheck } from '../utils/manifest';
import { Countdown } from './Countdown';

interface Props {
  session: PublicSession;
  option: PaymentOption;
}

export function PaymentPanel({ session, option }: Props) {
  const [qr, setQr] = useState<string>('');
  const [verify, setVerify] = useState<ManifestCheck>('unverified');
  const meta = CHAINS[option.chain];

  useEffect(() => {
    let alive = true;
    QRCode.toDataURL(option.qr_payload || option.uri || option.address, {
      errorCorrectionLevel: 'M',
      margin: 1,
      width: 320,
      color: { dark: '#0e1726', light: '#ffffff' },
    })
      .then((url) => alive && setQr(url))
      .catch(() => alive && setQr(''));
    return () => {
      alive = false;
    };
  }, [option.qr_payload, option.uri, option.address]);

  useEffect(() => {
    let alive = true;
    void checkOption(session.address_manifest, option).then((r) => alive && setVerify(r));
    return () => {
      alive = false;
    };
  }, [session.address_manifest, option]);

  const amount = trimDecimal(option.amount);

  return (
    <section aria-label={t('pay.title')}>
      <div class="cv-pay">
        <div>
          <div class="cv-qr">
            {qr ? (
              <img src={qr} alt={t('a11y.qr', { asset: option.symbol })} />
            ) : (
              <div class="cv-spinner" aria-hidden="true" />
            )}
          </div>
          <p class="cv-hint" style={{ textAlign: 'center', marginTop: 6 }}>
            {t('pay.scan')}
          </p>
          {isMobile() && option.uri && (
            <a class="cv-btn" href={option.uri} style={{ marginTop: 8 }}>
              {t('pay.openInWallet')}
            </a>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <CopyField
            id="cv-amount"
            label={`${t('pay.amount')} (${option.symbol})`}
            value={amount}
            copyValue={amount}
            big
            copyLabel={t('pay.copyAmount')}
          />
          <CopyField
            id="cv-address"
            label={`${t('pay.address')} · ${meta.label}`}
            value={option.address}
            copyValue={option.address}
            copyLabel={t('pay.copyAddress')}
          />
          {verify === 'verified' && <p class="cv-hint ok">{t('pay.verified', { merchant: session.merchant.name })}</p>}
          {verify === 'mismatch' && <p class="cv-hint danger">{t('pay.unverified')}</p>}
          <Countdown option={option} session={session} />
        </div>
      </div>
      <p class="cv-hint" style={{ marginTop: 12 }}>
        {t('pay.exactAmount')}
      </p>
      {option.token_contract && (
        <p class="cv-hint warn">{t('pay.tokenWarning', { asset: option.symbol, network: meta.label })}</p>
      )}
      {checkTokenContract(option.chain, option.symbol, option.token_contract) === 'mismatch' && (
        <p class="cv-hint danger" role="alert">
          {t('pay.tokenMismatch', { asset: option.symbol, network: meta.label })}
        </p>
      )}
    </section>
  );
}

interface CopyProps {
  id: string;
  label: string;
  value: string;
  copyValue: string;
  copyLabel: string;
  big?: boolean;
}

function CopyField({ id, label, value, copyValue, copyLabel, big }: CopyProps) {
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!done) return;
    const tm = setTimeout(() => setDone(false), 1800);
    return () => clearTimeout(tm);
  }, [done]);
  return (
    <div class="cv-field">
      <label for={id}>{label}</label>
      <div class="row">
        <output id={id} class={`value mono${big ? ' big' : ''}`}>
          {value}
        </output>
        <button
          type="button"
          class={`cv-copy${done ? ' done' : ''}`}
          aria-label={copyLabel}
          onClick={async () => {
            if (await copyText(copyValue)) setDone(true);
          }}
        >
          {done ? t('pay.copied') : t('pay.copy')}
        </button>
      </div>
      <span class="sr-only" role="status" aria-live="polite">
        {done ? t('a11y.copyDone') : ''}
      </span>
    </div>
  );
}
